<template>
  <div class="cl" :class="{ 'cl--calls': isCalls }">
    <!-- toolbar -->
    <div class="cl__bar">
      <div class="cl__search">
        <v-icon small>mdi-magnify</v-icon>
        <input v-model="f.search" :placeholder="isCalls ? $t('crmSearchCalls') : $t('crmSearchNotes')" @input="debounced">
      </div>
      <div class="cl__ranges">
        <button v-for="r in ranges" :key="r.key" type="button" :class="{ on: f.range === r.key }" @click="setRange(r.key)">{{ r.label }}</button>
      </div>
      <button type="button" class="cl__chip" :class="{ on: f.mine }" @click="set('mine', !f.mine)"><v-icon x-small>mdi-account-outline</v-icon>{{ $t('crmQuickMine') }}</button>
      <template v-if="isCalls">
        <button type="button" class="cl__chip" :class="{ on: f.overdue }" @click="set('overdue', !f.overdue)"><v-icon x-small>mdi-alarm</v-icon>{{ $t('crmOverdue') }}</button>
        <button type="button" class="cl__chip" :class="{ on: f.outcome.includes('no_answer') && f.outcome.length === 1 }" @click="set('outcome', f.outcome.length === 1 && f.outcome[0] === 'no_answer' ? [] : ['no_answer'])"><v-icon x-small>mdi-phone-missed-outline</v-icon>{{ $t('crmOut_no_answer') }}</button>
      </template>
      <template v-else>
        <button type="button" class="cl__chip" :class="{ on: f.mentions_me }" @click="set('mentions_me', !f.mentions_me)"><v-icon x-small>mdi-at</v-icon>{{ $t('crmMentionsMe') }}</button>
      </template>
      <v-menu v-model="menu" offset-y :close-on-content-click="false" min-width="340" max-width="380">
        <template #activator="{ on }">
          <button type="button" class="cl__chip" :class="{ on: activeCount }" v-on="on">
            <v-icon x-small>mdi-filter-variant</v-icon>{{ $t('crmFilters') }}
            <span v-if="activeCount" class="cl__badge">{{ activeCount }}</span>
          </button>
        </template>
        <div class="cl__panel">
          <div class="cl__panel-title">{{ $t('crmAdvancedFilter') }}</div>
          <div class="cl__grid">
            <div class="cl__pair">
              <date-picker :value="f.from" format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" :locale="dateLocale" :placeholder="$t('crmFromDate')" clearable @change="v => setMany({ from: v, range: 'custom' })" />
              <date-picker :value="f.to" format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" :locale="dateLocale" :placeholder="$t('crmToDate')" clearable @change="v => setMany({ to: v, range: 'custom' })" />
            </div>
            <crm-user-picker :value="f.owner_user_id" title="crmOwner" multiple @input="set('owner_user_id', $event)" />
            <v-select :value="f.status" :items="statusItems" :label="$t('crmStatus')" multiple outlined dense hide-details small-chips @change="set('status', $event)" />
            <v-select :value="f.entity_type" :items="entityItems" :label="$t('crmRelatedTo')" multiple outlined dense hide-details small-chips @change="set('entity_type', $event)" />
            <v-select v-if="pipelines.length" :value="f.pipeline_id" :items="pipelines" :label="$t('crmPipeline')" outlined dense hide-details clearable @change="set('pipeline_id', $event)" />
            <template v-if="isCalls">
              <v-select :value="f.direction" :items="directionItems" :label="$t('crmDirection')" multiple outlined dense hide-details small-chips @change="set('direction', $event)" />
              <v-select :value="f.outcome" :items="outcomeItems" :label="$t('crmOutcome')" multiple outlined dense hide-details small-chips @change="set('outcome', $event)" />
              <div class="cl__pair">
                <v-text-field :value="f.min_min" type="number" min="0" :label="$t('crmMinDurationMin')" outlined dense hide-details dir="ltr" @change="set('min_min', $event)" />
                <v-text-field :value="f.max_min" type="number" min="0" :label="$t('crmMaxDurationMin')" outlined dense hide-details dir="ltr" @change="set('max_min', $event)" />
              </div>
              <div class="cl__tri">
                <span>{{ $t('crmHasRecording') }}</span>
                <div class="cl__seg">
                  <button v-for="o in triItems" :key="String(o.v)" type="button" :class="{ on: f.has_recording === o.v }" @click="set('has_recording', o.v)">{{ o.t }}</button>
                </div>
              </div>
              <div class="cl__tri">
                <span>{{ $t('crmHasResult') }}</span>
                <div class="cl__seg">
                  <button v-for="o in triItems" :key="String(o.v)" type="button" :class="{ on: f.has_result === o.v }" @click="set('has_result', o.v)">{{ o.t }}</button>
                </div>
              </div>
            </template>
            <template v-else>
              <v-switch :input-value="f.has_mentions" inset dense hide-details :label="$t('crmHasMentions')" @change="set('has_mentions', $event)" />
            </template>
            <v-switch :input-value="f.without_entity" inset dense hide-details :label="$t('crmWithoutEntity')" @change="set('without_entity', $event)" />
            <div class="cl__pair">
              <v-select :value="f.sort" :items="sortItems" :label="$t('crmSortBy')" outlined dense hide-details @change="set('sort', $event)" />
              <v-select :value="f.direction_sort" :items="sortDirItems" :label="$t('crmSortDir')" outlined dense hide-details @change="set('direction_sort', $event)" />
            </div>
          </div>
          <div class="cl__panel-foot">
            <v-btn small text color="primary" @click="clear">{{ $t('crmClearFilters') }}</v-btn>
            <v-btn small depressed color="primary" @click="menu = false">{{ $t('crmApply') }}</v-btn>
          </div>
        </div>
      </v-menu>
      <button v-if="activeCount || f.search" type="button" class="cl__clear" @click="clear">{{ $t('crmClearFilters') }}</button>
      <v-spacer />
      <span class="cl__total">{{ $t('crmTotalRecords', { n: num(total) }) }}</span>
    </div>

    <!-- call summary (same filters as the list) -->
    <section v-if="isCalls && summary" class="cl__sum">
      <div class="cl__kpis">
        <div class="cl__kpi"><span>{{ $t('crmCallsTotal') }}</span><b>{{ num(summary.total) }}</b><small>{{ $t('crmActDone') }} {{ num(summary.done) }} · {{ $t('crmScheduled') }} {{ num(summary.planned) }}</small></div>
        <div class="cl__kpi cl__kpi--ok"><span>{{ $t('crmAnswerRate') }}</span><b>{{ pct(summary.answer_rate) }}</b><small>{{ $t('crmOut_answered') }} {{ num(summary.answered) }}</small></div>
        <div class="cl__kpi"><span>{{ $t('crmTalkTime') }}</span><b>{{ dur(summary.total_duration_seconds, true) }}</b><small>{{ $t('crmAvg') }} {{ dur(summary.avg_duration_seconds) }}</small></div>
        <div class="cl__kpi"><span>{{ $t('crmInOut') }}</span><b>{{ num(summary.outbound) }} / {{ num(summary.inbound) }}</b><small>{{ $t('crmDir_outbound') }} / {{ $t('crmDir_inbound') }}</small></div>
        <div class="cl__kpi" :class="{ 'cl__kpi--bad': summary.overdue }"><span>{{ $t('crmOverdue') }}</span><b>{{ num(summary.overdue) }}</b><small>{{ $t('crmCancelledN', { n: num(summary.cancelled) }) }}</small></div>
        <button type="button" class="cl__more" @click="showCharts = !showCharts">
          <v-icon small>{{ showCharts ? 'mdi-chevron-up' : 'mdi-chart-box-outline' }}</v-icon>{{ showCharts ? $t('crmHideAnalysis') : $t('crmShowAnalysis') }}
        </button>
      </div>

      <div v-if="showCharts" class="cl__charts">
        <div class="cl__card">
          <div class="cl__card-title">{{ $t('crmByOutcome') }}</div>
          <button v-for="o in outcomeBars" :key="o.key" type="button" class="cl__hbar" :title="$t('crmFilterBy')" @click="set('outcome', [o.key])">
            <span class="cl__hbar-l">{{ o.label }}</span>
            <span class="cl__hbar-track"><i :style="{ width: o.pct + '%', background: o.color }" /></span>
            <b>{{ num(o.n) }}</b>
          </button>
        </div>
        <div class="cl__card">
          <div class="cl__card-title">{{ $t('crmBestHour') }}</div>
          <div class="cl__hours">
            <div v-for="h in hours" :key="h.hour" class="cl__hour" :title="h.hour + ':00 — ' + num(h.total) + ' / ' + num(h.answered)">
              <div class="cl__hour-bar">
                <i class="t" :style="{ height: h.tp + '%' }" />
                <i class="a" :style="{ height: h.ap + '%' }" />
              </div>
              <small>{{ h.hour }}</small>
            </div>
          </div>
          <div class="cl__legend"><span class="t" />{{ $t('crmCallsTotal') }}<span class="a" />{{ $t('crmOut_answered') }}</div>
        </div>
        <div class="cl__card cl__card--wide">
          <div class="cl__card-title">{{ $t('crmByOwner') }}</div>
          <table class="cl__tbl">
            <thead>
              <tr><th>{{ $t('crmOwner') }}</th><th>{{ $t('crmCallsTotal') }}</th><th>{{ $t('crmAnswerRate') }}</th><th>{{ $t('crmDir_outbound') }}</th><th>{{ $t('crmDir_inbound') }}</th><th>{{ $t('crmTalkTime') }}</th><th>{{ $t('crmAvg') }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="o in summary.by_owner || []" :key="o.owner_user_id" @click="set('owner_user_id', [o.owner_user_id])">
                <td><span class="cl__owner"><crm-avatar :name="o.name" :size="22" />{{ o.name }}</span></td>
                <td>{{ num(o.total) }}</td>
                <td><span class="cl__rate"><i :style="{ width: (o.answer_rate || 0) + '%' }" /></span>{{ pct(o.answer_rate) }}</td>
                <td>{{ num(o.outbound) }}</td>
                <td>{{ num(o.inbound) }}</td>
                <td>{{ dur(o.total_duration_seconds, true) }}</td>
                <td>{{ dur(o.avg_duration_seconds) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="(summary.top_customers || []).length" class="cl__card">
          <div class="cl__card-title">{{ $t('crmTopCustomers') }}</div>
          <button v-for="c in summary.top_customers" :key="c.entity_type + c.entity_id" type="button" class="cl__top" @click="openEntity({ type: c.entity_type, id: c.entity_id })">
            <crm-avatar :name="(c.entity && c.entity.title) || '?'" :size="24" />
            <span class="cl__top-t">{{ (c.entity && c.entity.title) || '#' + c.entity_id }}</span>
            <small>{{ fmtDate(c.last_call_at) }}</small>
            <b>{{ num(c.total) }}</b>
          </button>
        </div>
      </div>
    </section>

    <!-- list -->
    <div class="cl__body" @scroll="onScroll">
      <template v-if="isCalls">
        <div v-for="a in rows" :key="a.id" class="cl__row" :class="{ 'cl__row--open': a.status === 'open', 'cl__row--late': isLate(a) }">
          <span class="cl__dir" :class="'cl__dir--' + (a.direction || 'outbound')" :title="$t('crmDir_' + (a.direction || 'outbound'))">
            <v-icon x-small>{{ a.status === 'open' ? 'mdi-phone-clock' : (a.direction === 'inbound' ? 'mdi-phone-incoming' : 'mdi-phone-outgoing') }}</v-icon>
          </span>
          <div class="cl__main">
            <div class="cl__line1">
              <button v-if="a.entity" type="button" class="cl__ent" @click="openEntity(a.entity)">{{ a.entity.title || ('#' + a.entity.id) }}</button>
              <span v-else class="cl__muted">{{ $t('crmNoEntity') }}</span>
              <span v-if="a.entity && a.entity.stage" class="cl__stage">{{ a.entity.stage }}</span>
              <span v-if="a.entity && a.entity.mobile" class="cl__ltr cl__muted">{{ a.entity.mobile }}</span>
            </div>
            <div class="cl__line2">{{ a.result || a.title || '—' }}</div>
          </div>
          <span v-if="a.outcome" class="cl__out" :style="tone(outcomeColor(a.outcome))">{{ outcomeLabel(a.outcome) }}</span>
          <span v-else-if="a.status === 'open'" class="cl__out" :style="tone('#d9822b')">{{ $t('crmScheduled') }}</span>
          <span class="cl__dur cl__hide-sm">{{ a.duration_seconds ? dur(a.duration_seconds) : '' }}</span>
          <a v-if="a.recording_url" :href="a.recording_url" target="_blank" rel="noopener" class="cl__rec" :title="$t('crmRecording')"><v-icon x-small>mdi-play-circle-outline</v-icon></a>
          <span class="cl__owner cl__hide-sm"><crm-avatar :user-id="a.owner_user_id" :name="a.owner_name" :size="22" />{{ a.owner_name || memberName(a.owner_user_id) }}</span>
          <span class="cl__when" :class="{ late: isLate(a) }">{{ fmtDate(a.completed_at || a.start_at || a.due_at || a.created_at, true) }}</span>
          <a v-if="a.entity && a.entity.mobile" :href="'tel:' + a.entity.mobile" class="cl__call" @click="callAgain(a)"><v-icon x-small>mdi-phone</v-icon></a>
        </div>
      </template>
      <template v-else>
        <article v-for="n in rows" :key="n.id" class="cl__note">
          <header>
            <crm-avatar :user-id="n.owner_user_id" :name="n.owner_name" :size="28" />
            <div class="cl__note-who">
              <b>{{ n.owner_name || memberName(n.owner_user_id) }}</b>
              <small>{{ fmtDate(n.completed_at || n.created_at, true) }}</small>
            </div>
            <v-spacer />
            <button v-if="n.entity" type="button" class="cl__ent cl__ent--tag" @click="openEntity(n.entity)">
              <v-icon x-small>{{ n.entity.type === 'lead' ? 'mdi-account-outline' : (n.entity.type === 'deal' ? 'mdi-briefcase-outline' : 'mdi-domain') }}</v-icon>{{ n.entity.title || ('#' + n.entity.id) }}
            </button>
          </header>
          <div class="cl__note-text">{{ n.content || n.result || n.title }}</div>
          <footer v-if="(n.participants || []).length || (n.mentions || []).length">
            <span v-for="p in (n.participants || n.mentions || [])" :key="p.id || p" class="cl__mention">@{{ p.name || memberName(p.id || p) }}</span>
          </footer>
        </article>
      </template>

      <div v-if="loading" class="cl__loading"><v-progress-circular indeterminate color="primary" :size="22" :width="2" /></div>
      <div v-else-if="!rows.length" class="cl__empty">
        <v-icon size="40" color="primary">{{ isCalls ? 'mdi-phone-log-outline' : 'mdi-note-multiple-outline' }}</v-icon>
        <b>{{ isCalls ? $t('crmNoCalls') : $t('crmNoNotes') }}</b>
        <small>{{ $t('crmEmptyFilterHint') }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'jalali-moment'
import api, { crmError } from '../crmApi'
import { crm, pick, fmtDate, memberName, learnOwners, isFa } from '../crmStore'
import CrmUserPicker from '../components/CrmUserPicker'
import { CrmAvatar, hexA } from '../components/CrmBits'

const OUTCOMES = ['answered', 'no_answer', 'busy', 'wrong_number', 'voicemail', 'callback', 'rejected', 'other']
const OUTCOME_COLORS = { answered: '#0f9d76', no_answer: '#d9822b', busy: '#e0a030', wrong_number: '#c0392b', voicemail: '#6a4fd1', callback: '#2166d1', rejected: '#b03a2e', other: '#8a94a6', unknown: '#9aa4b2' }
const SQL_DAY = 'YYYY-MM-DD'

function emptyFilters () {
  return {
    search: '',
    range: '30d',
    from: '',
    to: '',
    mine: false,
    owner_user_id: [],
    status: [],
    entity_type: [],
    pipeline_id: null,
    without_entity: false,
    sort: 'date',
    direction_sort: 'desc',
    direction: [],
    outcome: [],
    min_min: null,
    max_min: null,
    has_recording: null,
    has_result: null,
    overdue: false,
    mentions_me: false,
    has_mentions: false
  }
}

export default {
  name: 'CrmCommLogPage',
  components: { CrmUserPicker, CrmAvatar },
  inject: ['shell'],
  props: { kind: { type: String, default: 'calls' } },
  data () {
    let saved = {}
    try { saved = JSON.parse(localStorage.getItem('crm-' + this.kind + '-filters')) || {} } catch (e) { saved = {} }
    return { f: { ...emptyFilters(), ...saved, search: '' }, rows: [], total: 0, page: 1, lastPage: 1, loading: false, summary: null, showCharts: false, menu: false, timer: null, outcomes: OUTCOMES }
  },
  computed: {
    isCalls () { return this.kind === 'calls' },
    dateLocale () { return isFa() ? 'fa' : 'en' },
    ranges () {
      return [
        { key: 'today', label: this.$t('crmGroupToday') },
        { key: '7d', label: this.$t('crmLast7') },
        { key: '30d', label: this.$t('crmLast30') },
        { key: 'month', label: this.$t('crmThisMonth') },
        { key: 'all', label: this.$t('crmAll') }
      ]
    },
    statusItems () { return [{ value: 'done', text: this.$t('crmActDone') }, { value: 'open', text: this.$t('crmScheduled') }, { value: 'cancelled', text: this.$t('crmCancelled') }] },
    entityItems () { return ['lead', 'deal', 'person', 'company'].map(e => ({ value: e, text: this.$t('crmEntity_' + e) })) },
    directionItems () { return ['outbound', 'inbound'].map(d => ({ value: d, text: this.$t('crmDir_' + d) })) },
    outcomeItems () { return this.outcomes.concat('unknown').map(o => ({ value: o, text: this.outcomeLabel(o) })) },
    pipelines () { return crm.pipelines.map(p => ({ value: p.id, text: p.title })) },
    triItems () { return [{ v: null, t: this.$t('crmAll') }, { v: 1, t: this.$t('crmYes') }, { v: 0, t: this.$t('crmNo') }] },
    sortItems () {
      const l = [{ value: 'date', text: this.$t('crmSortDate') }, { value: 'created', text: this.$t('crmCreatedAt') }, { value: 'owner', text: this.$t('crmOwner') }]
      if (this.isCalls) l.splice(1, 0, { value: 'duration', text: this.$t('crmDuration') })
      return l
    },
    sortDirItems () { return [{ value: 'desc', text: this.$t('crmNewestFirst') }, { value: 'asc', text: this.$t('crmOldestFirst') }] },
    activeCount () {
      const f = this.f
      return ['owner_user_id', 'status', 'entity_type', 'direction', 'outcome'].filter(k => f[k] && f[k].length).length +
        ['pipeline_id', 'min_min', 'max_min', 'without_entity', 'has_mentions'].filter(k => f[k]).length +
        ['has_recording', 'has_result'].filter(k => f[k] !== null && f[k] !== undefined).length +
        (f.range === 'custom' ? 1 : 0)
    },
    outcomeBars () {
      const by = (this.summary && this.summary.by_outcome) || {}
      const max = Math.max(1, ...Object.values(by).map(Number))
      return Object.keys(by).sort((a, b) => by[b] - by[a]).map(k => ({ key: k, n: by[k], pct: Math.round(by[k] / max * 100), label: this.outcomeLabel(k), color: this.outcomeColor(k) }))
    },
    hours () {
      const list = (this.summary && this.summary.by_hour) || []
      const map = {}
      list.forEach(h => { map[h.hour] = h })
      const max = Math.max(1, ...list.map(h => h.total || 0))
      const out = []
      for (let h = 8; h <= 20; h++) {
        const x = map[h] || { total: 0, answered: 0 }
        out.push({ hour: h, total: x.total, answered: x.answered, tp: Math.round((x.total || 0) / max * 100), ap: Math.round((x.answered || 0) / max * 100) })
      }
      return out
    }
  },
  created () {
    this.shell.$on('changed', this.reload)
    this.reload()
  },
  beforeDestroy () { this.shell.$off('changed', this.reload) },
  methods: {
    fmtDate,
    memberName,
    num (v) { return Number(v || 0).toLocaleString(isFa() ? 'fa-IR' : 'en-US') },
    pct (v) { return (v === null || v === undefined) ? '—' : Number(v).toLocaleString(isFa() ? 'fa-IR' : 'en-US', { maximumFractionDigits: 1 }) + '٪' },
    dur (sec, long = false) {
      const s = Math.round(Number(sec) || 0)
      if (!s) return '—'
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const ss = s % 60
      if (long && h) return this.$t('crmHoursMinutes', { h: this.num(h), m: this.num(m) })
      return (h ? h + ':' + String(m).padStart(2, '0') : m) + ':' + String(ss).padStart(2, '0')
    },
    outcomeLabel (o) { return this.$t(o === 'wrong_number' ? 'crmOut_wrong' : (o === 'callback' ? 'crmOut_call_back' : 'crmOut_' + o)) },
    outcomeColor (o) { return OUTCOME_COLORS[o] || '#8a94a6' },
    tone (c) { return { color: c, background: hexA(c, 0.12) } },
    isLate (a) { return a.status === 'open' && a.due_at && moment(a.due_at, 'YYYY-MM-DD HH:mm:ss').isBefore(moment()) },
    set (k, v) { this.setMany({ [k]: v }) },
    setMany (patch) {
      this.f = { ...this.f, ...patch }
      const { search, ...keep } = this.f
      try { localStorage.setItem('crm-' + this.kind + '-filters', JSON.stringify(keep)) } catch (e) { /* ignore */ }
      this.reload()
    },
    setRange (key) { this.setMany({ range: key, from: '', to: '' }) },
    clear () { this.f = emptyFilters(); this.setMany({}) },
    debounced () {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reload, 350)
    },
    dateRange () {
      const f = this.f
      const t = moment().locale('en')
      if (f.range === 'custom') return { from: f.from || undefined, to: f.to || undefined }
      if (f.range === 'today') return { from: t.format(SQL_DAY), to: t.format(SQL_DAY) }
      if (f.range === '7d') return { from: t.clone().subtract(6, 'days').format(SQL_DAY), to: t.format(SQL_DAY) }
      if (f.range === '30d') return { from: t.clone().subtract(29, 'days').format(SQL_DAY), to: t.format(SQL_DAY) }
      if (f.range === 'month') {
        // Jalali month when the UI is Persian
        if (isFa()) {
          const j = moment().locale('fa')
          return { from: j.clone().startOf('jMonth').locale('en').format(SQL_DAY), to: j.clone().endOf('jMonth').locale('en').format(SQL_DAY) }
        }
        return { from: t.clone().startOf('month').format(SQL_DAY), to: t.clone().endOf('month').format(SQL_DAY) }
      }
      return {}
    },
    params () {
      const f = this.f
      const p = { ...this.dateRange(), sort: f.sort, direction_sort: f.direction_sort }
      if (f.search) p.search = f.search
      if (f.mine) p.mine = 1
      if (f.owner_user_id.length) p.owner_user_id = f.owner_user_id
      if (f.status.length) p.status = f.status
      if (f.entity_type.length) p.entity_type = f.entity_type
      if (f.pipeline_id) p.pipeline_id = f.pipeline_id
      if (f.without_entity) p.without_entity = 1
      if (this.isCalls) {
        if (f.direction.length) p.direction = f.direction
        if (f.outcome.length) p.outcome = f.outcome
        if (f.min_min) p.min_duration = Number(f.min_min) * 60
        if (f.max_min) p.max_duration = Number(f.max_min) * 60
        if (f.has_recording !== null && f.has_recording !== undefined) p.has_recording = f.has_recording
        if (f.has_result !== null && f.has_result !== undefined) p.has_result = f.has_result
        if (f.overdue) p.overdue = 1
      } else {
        if (f.mentions_me) p.mentions_me = 1
        if (f.has_mentions) p.has_mentions = 1
      }
      return p
    },
    reload () {
      this.page = 1
      this.rows = []
      this.load()
      if (this.isCalls) this.loadSummary()
    },
    async load () {
      this.loading = true
      try {
        const fn = this.isCalls ? api.calls : api.notes
        const res = await fn({ ...this.params(), page: this.page, itemsPerPage: 40 })
        const items = pick(res)
        learnOwners(items)
        this.rows = this.page === 1 ? items : this.rows.concat(items)
        this.total = res.total || this.rows.length
        this.lastPage = res.last_page || 1
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    async loadSummary () {
      try {
        this.summary = await api.callsSummary(this.params())
        ;((this.summary && this.summary.by_owner) || []).forEach(o => learnOwners([{ owner_user_id: o.owner_user_id, owner_name: o.name }]))
      } catch (e) { this.summary = null }
    },
    onScroll (e) {
      const el = e.target
      if (!this.loading && this.page < this.lastPage && el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
        this.page++
        this.load()
      }
    },
    openEntity (ent) {
      if (!ent) return
      if (ent.type === 'deal' || ent.type === 'lead') this.shell.openRecord(ent.type, { id: ent.id, title: ent.title }, 'activity')
    },
    callAgain (a) {
      if (a.entity && (a.entity.type === 'deal' || a.entity.type === 'lead')) {
        this.shell.logActivity(a.entity.type, { id: a.entity.id, title: a.entity.title, mobile: a.entity.mobile }, 'call', 'done')
      }
    }
  }
}
</script>

<style scoped>
.cl { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }
.cl__bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex-shrink: 0; }
.cl__search { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .1); width: 240px; }
.cl__search:focus-within { border-color: var(--v-primary-base); box-shadow: 0 0 0 3px rgba(25, 118, 210, .12); }
.theme--dark .cl__search { background: #1e1e1e; }
.cl__search input { flex: 1; min-width: 0; outline: none; font-size: .85rem; color: inherit; }
.cl__ranges { display: inline-flex; padding: 3px; border-radius: 6px; background: rgba(15, 23, 42, .06); }
.cl__ranges button { height: 30px; padding: 0 10px; border-radius: 6px; font-size: .78rem; font-weight: 600; opacity: .65; }
.cl__ranges button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.theme--dark .cl__ranges button.on { background: #2b2b2b; }
.cl__chip { display: inline-flex; align-items: center; gap: 5px; height: 30px; padding: 0 10px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); font-size: .76rem; background: #fff; }
.theme--dark .cl__chip { background: #1e1e1e; }
.cl__chip.on { border-color: var(--v-primary-base); background: rgba(25, 118, 210, .09); color: var(--v-primary-base); }
.cl__chip.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.cl__badge { min-width: 18px; height: 18px; border-radius: 50%; background: var(--v-primary-base); color: #fff; font-size: .68rem; display: inline-flex; align-items: center; justify-content: center; }
.cl__clear { font-size: .76rem; color: var(--v-primary-base); }
.cl__total { font-size: .78rem; opacity: .65; }
.cl__new { border-radius: 6px !important; letter-spacing: 0; height: 34px !important; }
.cl__panel { background: #fff; padding: 14px; border-radius: 6px; }
.theme--dark .cl__panel { background: #1e1e1e; }
.cl__panel-title { font-weight: 700; font-size: .88rem; margin-bottom: 14px; }
.cl__grid { display: flex; flex-direction: column; gap: 14px; max-height: 60vh; overflow-y: auto; padding-top: 4px; }
.cl__pair { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.cl__tri { display: flex; align-items: center; justify-content: space-between; font-size: .8rem; }
.cl__seg { display: inline-flex; padding: 2px; border-radius: 6px; background: rgba(127, 127, 127, .1); }
.cl__seg button { height: 26px; padding: 0 10px; border-radius: 6px; font-size: .74rem; opacity: .7; }
.cl__seg button.on { background: #fff; opacity: 1; color: var(--v-primary-base); font-weight: 600; box-shadow: 0 1px 2px rgba(15, 23, 42, .12); }
.cl__panel-foot { display: flex; justify-content: space-between; margin-top: 14px; }
.cl__panel ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.cl__panel ::v-deep fieldset { border-radius: 6px; }
.cl__sum { flex-shrink: 0; display: flex; flex-direction: column; gap: 10px; }
.cl__kpis { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)) auto; gap: 8px; }
.cl__kpi { display: flex; flex-direction: column; padding: 10px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); }
.theme--dark .cl__kpi { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.cl__kpi span { font-size: .72rem; opacity: .6; }
.cl__kpi b { font-size: 1.15rem; font-weight: 800; line-height: 1.5; }
.cl__kpi small { font-size: .7rem; opacity: .7; }
.cl__kpi--ok b { color: #0f9d76; }
.cl__kpi--bad b { color: #c0392b; }
.cl__more { display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; padding: 0 14px; border-radius: 6px; border: 1px dashed rgba(25, 118, 210, .4); color: var(--v-primary-base); font-size: .74rem; font-weight: 600; }
.cl__more ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.cl__charts { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; max-height: 46vh; overflow-y: auto; }
.cl__card { background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; padding: 12px 14px; }
.theme--dark .cl__card { background: #1e1e1e; }
.cl__card--wide { grid-column: 1 / -1; }
.cl__card-title { font-weight: 700; font-size: .84rem; margin-bottom: 10px; }
.cl__hbar { display: flex; align-items: center; gap: 8px; width: 100%; padding: 3px 0; font-size: .76rem; text-align: start; }
.cl__hbar:hover .cl__hbar-l { color: var(--v-primary-base); }
.cl__hbar-l { width: 120px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cl__hbar-track { flex: 1; height: 8px; border-radius: 4px; background: rgba(127, 127, 127, .12); overflow: hidden; }
.cl__hbar-track i { display: block; height: 100%; border-radius: 4px; }
.cl__hbar b { min-width: 34px; text-align: end; }
.cl__hours { display: flex; align-items: flex-end; gap: 4px; height: 110px; }
.cl__hour { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; height: 100%; }
.cl__hour-bar { position: relative; flex: 1; width: 100%; max-width: 22px; }
.cl__hour-bar i { position: absolute; bottom: 0; inset-inline: 0; border-radius: 3px 3px 0 0; }
.cl__hour-bar .t { background: rgba(25, 118, 210, .2); }
.cl__hour-bar .a { background: #0f9d76; inset-inline: 25%; }
.cl__hour small { font-size: .64rem; opacity: .6; }
.cl__legend { display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: .7rem; opacity: .75; }
.cl__legend span { width: 10px; height: 10px; border-radius: 3px; display: inline-block; margin-inline-start: 8px; }
.cl__legend .t { background: rgba(25, 118, 210, .3); }
.cl__legend .a { background: #0f9d76; }
.cl__tbl { width: 100%; border-collapse: collapse; font-size: .78rem; }
.cl__tbl th { text-align: start; font-weight: 700; opacity: .65; padding: 6px 8px; border-bottom: 1px solid rgba(15, 23, 42, .08); white-space: nowrap; }
.cl__tbl td { padding: 7px 8px; border-bottom: 1px solid rgba(15, 23, 42, .05); white-space: nowrap; }
.cl__tbl tbody tr { cursor: pointer; }
.cl__tbl tbody tr:hover { background: rgba(25, 118, 210, .05); }
.cl__rate { display: inline-block; width: 40px; height: 4px; border-radius: 4px; background: rgba(127, 127, 127, .15); overflow: hidden; vertical-align: middle; margin-inline-end: 6px; }
.cl__rate i { display: block; height: 100%; background: #0f9d76; }
.cl__top { display: flex; align-items: center; gap: 8px; width: 100%; padding: 5px 0; font-size: .78rem; text-align: start; }
.cl__top-t { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.cl__top small { opacity: .6; }
.cl__body { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding-bottom: 20px; }
.cl__row { display: flex; align-items: center; gap: 12px; padding: 9px 14px; background: #fff; border: 1px solid rgba(15, 23, 42, .07); border-radius: 6px; margin-bottom: 6px; }
.theme--dark .cl__row { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.cl__row:hover { box-shadow: 0 3px 10px rgba(15, 23, 42, .07); }
.cl__row--late { border-inline-start: 3px solid #c0392b; }
.cl__dir { width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; background: rgba(25, 118, 210, .1); }
.cl__dir--inbound { background: rgba(15, 157, 118, .12); }
.cl__dir--inbound ::v-deep .v-icon { color: #0f9d76 !important; }
.cl__dir--outbound ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.cl__row--open .cl__dir { background: rgba(217, 130, 43, .14); }
.cl__row--open .cl__dir ::v-deep .v-icon { color: #d9822b !important; }
.cl__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.cl__line1 { display: flex; align-items: center; gap: 8px; font-size: .84rem; min-width: 0; }
.cl__line2 { font-size: .76rem; opacity: .7; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cl__ent { font-weight: 700; color: inherit; text-align: start; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cl__ent:hover { color: var(--v-primary-base); text-decoration: underline; }
.cl__ent--tag { display: inline-flex; align-items: center; gap: 4px; font-size: .74rem; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: rgba(25, 118, 210, .08); color: var(--v-primary-base); }
.cl__stage { font-size: .68rem; padding: 0 6px; border-radius: 6px; background: rgba(127, 127, 127, .12); }
.cl__muted { opacity: .55; font-size: .76rem; }
.cl__ltr { direction: ltr; display: inline-block; }
.cl__out { font-size: .7rem; font-weight: 700; padding: 2px 8px; border-radius: 6px; white-space: nowrap; }
.cl__dur { font-size: .76rem; font-variant-numeric: tabular-nums; min-width: 44px; text-align: end; opacity: .8; direction: ltr; }
.cl__rec { width: 26px; height: 26px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; background: rgba(106, 79, 209, .1); text-decoration: none; }
.cl__rec ::v-deep .v-icon { color: #6a4fd1 !important; }
.cl__owner { display: inline-flex; align-items: center; gap: 6px; font-size: .76rem; white-space: nowrap; }
.cl__row .cl__owner { min-width: 120px; }
.cl__when { font-size: .74rem; opacity: .7; white-space: nowrap; min-width: 100px; text-align: end; }
.cl__when.late { color: #c0392b; opacity: 1; font-weight: 600; }
.cl__call { width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 157, 118, .12); text-decoration: none; }
.cl__call ::v-deep .v-icon { color: #0f9d76 !important; }
.cl__note { background: #fff; border: 1px solid rgba(15, 23, 42, .07); border-radius: 6px; padding: 12px 14px; margin-bottom: 8px; }
.theme--dark .cl__note { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.cl__note header { display: flex; align-items: center; gap: 10px; }
.cl__note-who { display: flex; flex-direction: column; line-height: 1.3; }
.cl__note-who b { font-size: .82rem; }
.cl__note-who small { font-size: .7rem; opacity: .6; }
.cl__note-text { margin-top: 8px; font-size: .86rem; line-height: 1.8; white-space: pre-line; }
.cl__note footer { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.cl__mention { font-size: .72rem; padding: 1px 7px; border-radius: 6px; background: rgba(106, 79, 209, .1); color: #6a4fd1; }
.cl__loading { display: flex; justify-content: center; padding: 16px; }
.cl__empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 0; text-align: center; }
.cl__empty small { opacity: .6; }
@media (max-width: 1100px) {
  .cl__kpis { grid-template-columns: repeat(3, 1fr); }
  .cl__charts { grid-template-columns: 1fr; }
}
@media (max-width: 799px) {
  .cl__search { width: 100%; }
  .cl__kpis { display: flex; overflow-x: auto; }
  .cl__kpi { flex: 0 0 140px; }
  .cl__hide-sm { display: none !important; }
  .cl__when { min-width: 0; }
}
</style>
